import { Character } from '../types/Character';
import { Zone } from '../types/Town';
import { Project, SoftwareTask } from '../types/Project';

export const initialZones: Zone[] = [
  { 
    id: 'meeting_room', 
    type: 'meeting_room', 
    name: 'Sala de Juntas', 
    purpose: 'Standups, planning, retrospectivas, triage de bugs',
    triggers: 'Cuando 2+ agentes necesitan alinear decisiones',
    position: { x: 2, y: 2, width: 8, height: 6 } 
  },
  { 
    id: 'open_office', 
    type: 'open_office', 
    name: 'Zona de Escritorios', 
    purpose: 'Trabajo individual: coding, diseño, documentación',
    triggers: 'Cuando un agente tiene una tarea asignada',
    position: { x: 12, y: 2, width: 18, height: 10 } 
  },
  { 
    id: 'review_corner', 
    type: 'review_corner', 
    name: 'Zona de Code Review', 
    purpose: 'Reviews de código, pair programming, debugging conjunto',
    triggers: 'Cuando una tarea pasa a estado review',
    position: { x: 2, y: 12, width: 8, height: 6 } 
  },
  { 
    id: 'cafeteria', 
    type: 'cafeteria', 
    name: 'Cafetería', 
    purpose: 'Conversaciones casuales, brainstorming informal',
    triggers: 'Breaks programados, o cuando un agente lleva >2h trabajando',
    position: { x: 16, y: 16, width: 14, height: 8 } 
  },
];

export const initialProject: Project = {
  id: 'proj_crm',
  name: 'CRM Nova Logistics',
  description: 'Sistema CRM para la gestión de clientes, envíos y generación de guías de paquetería.',
  status: 'active',
  createdAt: new Date(),
};

export const initialTasks: SoftwareTask[] = [
  {
    id: 'task_auth',
    title: 'Autenticación con Firebase',
    description: 'Implementar login de usuarios usando Firebase Auth',
    assignedTo: 'bob_frontend',
    status: 'in_progress',
    priority: 'high',
    dependencies: [],
    createdBy: 'alice_pm',
    history: [],
    createdAt: new Date()
  },
  {
    id: 'task_api',
    title: 'Rate Limiting API de Paquetería',
    description: 'Arreglar el límite de peticiones de la API externa de cotizaciones',
    assignedTo: 'charlie_backend',
    status: 'review',
    priority: 'critical',
    dependencies: [],
    createdBy: 'alice_pm',
    history: [],
    createdAt: new Date()
  }
];

export const initialCharacters: Character[] = [
  {
    id: 'alice_pm',
    name: 'Alice',
    sprite: 'https://opengameart.org/sites/default/files/teacher_pixel.png', // Temporary sprite
    role: 'Product Manager',
    personality: 'Meticulosa y orientada a deadlines. Prioriza features por impacto de negocio, no por complejidad técnica. Tiende a presionar al equipo cuando se acerca la fecha de entrega. Confía en datos, no en intuición. Cuando un bug es reportado, su primera pregunta es ¿cuántos usuarios afecta? antes de ¿qué lo causó?',
    skills: ['requirement_analysis', 'prioritization', 'stakeholder_comm'],
    communicationStyle: 'directa, usa métricas, hace preguntas incómodas',
    position: { x: 4, y: 4 },
    memories: [
      { description: "El cliente necesita el módulo de cotizaciones listo para fin de mes.", importance: 9, type: 'observation', timestamp: new Date() },
      { description: "Bob mencionó que la integración con la API externa de paquetería tiene problemas de rate limiting.", importance: 7, type: 'conversation', relatedAgents: ['bob_frontend'], timestamp: new Date() },
      { description: "Debemos priorizar el flujo de emisión de guías sobre el dashboard analítico.", importance: 8, type: 'reflection', timestamp: new Date() }
    ],
    currentTask: null,
    dailySchedule: [],
    status: 'idle',
    lastTaskCompletedAt: new Date(),
    version: "1.0.0"
  },
  {
    id: 'bob_frontend',
    name: 'Bob',
    sprite: 'https://opengameart.org/sites/default/files/merchant_pixel.png',
    role: 'Frontend Developer',
    personality: 'Perfeccionista con el código UI. Se frustra cuando le piden cambios de último momento sin entender el impacto en la arquitectura del frontend. Defiende la experiencia del usuario incluso cuando va en contra del timeline. Prefiere refactorizar antes que parchear.',
    skills: ['react', 'css', 'ux', 'state_management'],
    communicationStyle: 'técnico, referencia componentes específicos, argumenta con UX',
    position: { x: 14, y: 4 },
    memories: [
      { description: "El diseño del formulario de cotizaciones no es responsivo, necesito revisarlo.", importance: 6, type: 'observation', timestamp: new Date() },
      { description: "Charlie aún no termina el endpoint de creación de usuarios.", importance: 8, type: 'conversation', relatedAgents: ['charlie_backend'], timestamp: new Date() }
    ],
    currentTask: 'task_auth',
    dailySchedule: [],
    status: 'working',
    lastTaskCompletedAt: new Date(),
    version: "1.0.0"
  },
  {
    id: 'charlie_backend',
    name: 'Charlie',
    sprite: 'https://opengameart.org/sites/default/files/student_pixel.png',
    role: 'Backend Developer',
    personality: 'Pragmático y callado. Le preocupa el performance de la base de datos. Se queja de que el frontend hace peticiones ineficientes. Suele resolver problemas rápido pero a veces olvida escribir tests.',
    skills: ['node', 'firebase', 'api_design', 'database_optimization'],
    communicationStyle: 'breve, directo al punto, menciona latencia y logs',
    position: { x: 18, y: 6 },
    memories: [
      { description: "Implementé un caché temporal para mitigar el rate limiting de la API externa.", importance: 7, type: 'observation', timestamp: new Date() },
      { description: "Alice me presionó por el endpoint de auth, pero la estructura de datos no estaba clara.", importance: 5, type: 'reflection', timestamp: new Date() }
    ],
    currentTask: 'task_api',
    dailySchedule: [],
    status: 'reviewing',
    lastTaskCompletedAt: new Date(),
    version: "1.0.0"
  },
  {
    id: 'diana_qa',
    name: 'Diana',
    sprite: 'https://opengameart.org/sites/default/files/chef_pixel.png',
    role: 'QA Engineer',
    personality: 'Detallista al extremo y un poco cínica. No confía en el código de nadie hasta probarlo 10 veces. Le encanta encontrar edge cases que rompen el sistema.',
    skills: ['testing', 'bug_hunting', 'automation'],
    communicationStyle: 'sarcástica, aporta pasos para reproducir bugs, exige casos de prueba',
    position: { x: 4, y: 14 },
    memories: [
      { description: "Encontré un bug crítico en producción: si un usuario presiona cotizar dos veces, se cobra doble.", importance: 10, type: 'observation', timestamp: new Date() },
      { description: "Le advertí a Bob que su componente de formulario fallaba en Firefox, pero me ignoró.", importance: 8, type: 'reflection', relatedAgents: ['bob_frontend'], timestamp: new Date() }
    ],
    currentTask: null,
    dailySchedule: [],
    status: 'idle',
    lastTaskCompletedAt: new Date(),
    version: "1.0.0"
  }
];
